import { useState } from 'react';
import { api } from '../../services/api';
import { Users, Phone, PencilSimple, X, Plus } from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { toast } from 'sonner';

const EMAIL_LABELS = ['Padre', 'Madre', 'Tutore', 'Alunno', 'Altro'];

export const StudentsList = ({ students, lessons, onUpdate }) => {
  const [showEdit, setShowEdit] = useState(false);
  const [editStudent, setEditStudent] = useState(null);
  const [form, setForm] = useState({ phone: '', phone2: '', emails: [] });

  const openEdit = (s) => {
    setEditStudent(s);
    setForm({ phone: s.phone || '', phone2: s.phone2 || '', emails: s.emails?.length ? [...s.emails] : [] });
    setShowEdit(true);
  };

  const addEmail = () => setForm({ ...form, emails: [...form.emails, { address: '', label: 'Padre' }] });
  const removeEmail = (i) => setForm({ ...form, emails: form.emails.filter((_, idx) => idx !== i) });

  const handleSave = async (e) => {
    e.preventDefault();
    const cleanEmails = form.emails.filter(em => em.address.trim());
    try {
      await api.updateStudent(editStudent.id, { phone: form.phone, phone2: form.phone2, emails: cleanEmails });
      toast.success('Contatti aggiornati');
      setShowEdit(false);
      if (onUpdate) onUpdate();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore aggiornamento'); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold font-['Work_Sans']">I Miei Alunni</h2>
        <div className="text-sm text-[#52525B]">{students.length} {students.length === 1 ? 'alunno' : 'alunni'}</div>
      </div>

      {students.length === 0 ? (
        <div className="text-center py-12 bg-white border border-[#E4E4E7] rounded-sm">
          <Users size={48} className="mx-auto text-[#E4E4E7] mb-4" />
          <p className="text-[#52525B]">Nessun alunno registrato</p>
        </div>
      ) : (
        <div className="bg-white border border-[#E4E4E7] rounded-sm overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#E4E4E7] bg-[#FAFAFA]">
                <th className="text-left p-3 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Nome</th>
                <th className="text-left p-3 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Telefono</th>
                <th className="text-left p-3 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Email</th>
                <th className="text-left p-3 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Lezioni</th>
                <th className="text-right p-3 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {[...students].sort((a, b) => a.name.localeCompare(b.name, 'it')).map(student => {
                const studentLessons = lessons.filter(l => l.student_id === student.id);
                return (
                  <tr key={student.id} className="border-b border-[#E4E4E7] hover:bg-[#FAFAFA]" data-testid={`student-row-${student.id}`}>
                    <td className="p-3 font-medium uppercase text-sm">{student.name}</td>
                    <td className="p-3 text-sm text-[#52525B]">
                      {(student.phone || student.phone2) ? (
                        <div className="space-y-0.5">
                          {student.phone && <div className="flex items-center gap-1 text-xs"><Phone size={12} className="text-[#A1A1AA]" />{student.phone}</div>}
                          {student.phone2 && <div className="flex items-center gap-1 text-xs"><Phone size={12} className="text-[#A1A1AA]" />{student.phone2}</div>}
                        </div>
                      ) : <span className="text-[#D4D4D8]">-</span>}
                    </td>
                    <td className="p-3 text-sm text-[#52525B]">
                      {student.emails?.length > 0 ? student.emails.map((em, i) => em.address ? (
                        <div key={i} className="text-xs"><span className="text-[#A1A1AA]">{em.label}:</span> {em.address}</div>
                      ) : null) : <span className="text-[#D4D4D8]">-</span>}
                    </td>
                    <td className="p-3 text-sm text-[#52525B]">{studentLessons.length}/sett</td>
                    <td className="p-3 text-right">
                      <button onClick={() => openEdit(student)} className="p-1.5 rounded hover:bg-[#F4F4F5] text-[#52525B]" title="Modifica contatti" data-testid={`edit-student-${student.id}`}><PencilSimple size={16} /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit Modal */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="sm:max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-['Work_Sans']">Modifica Contatti</DialogTitle><DialogDescription>{editStudent?.name}</DialogDescription></DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label className="label-overline">Telefono 1</Label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="Telefono" className="rounded-sm" /></div>
              <div className="space-y-2"><Label className="label-overline">Telefono 2</Label><Input value={form.phone2} onChange={e => setForm({ ...form, phone2: e.target.value })} placeholder="Telefono 2" className="rounded-sm" /></div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between"><Label className="label-overline">Email</Label><Button type="button" variant="ghost" size="sm" onClick={addEmail} className="h-6 text-xs"><Plus size={12} className="mr-1" />Aggiungi</Button></div>
              {form.emails.map((em, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Select value={em.label} onValueChange={v => { const e2 = [...form.emails]; e2[i].label = v; setForm({ ...form, emails: e2 }); }}>
                    <SelectTrigger className="w-[100px] rounded-sm h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{EMAIL_LABELS.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input type="email" value={em.address} onChange={e => { const e2 = [...form.emails]; e2[i].address = e.target.value; setForm({ ...form, emails: e2 }); }} placeholder="email@..." className="rounded-sm h-8 text-sm flex-1" />
                  <button type="button" onClick={() => removeEmail(i)} className="p-1 text-red-500 hover:bg-red-50 rounded"><X size={14} /></button>
                </div>
              ))}
            </div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setShowEdit(false)} className="rounded-sm">Annulla</Button><Button type="submit" className="bg-[#0A0A0A] rounded-sm">Salva</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
